import mustache from "mustache";
import { AnalisisLaboratorioResponse } from "../../../../modulos/fertilizacion/response/AnalisisLaboratorioResponse";
import { ClienteFincaLoteResponse } from "../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import { nutrienteSimbolo } from "../../../../utils/fertilizacionUtils";
import { _2d } from "../../../../utils/numberUtils";
import {
  agregarPorOxido,
  faltantesPorOxido,
  GrupoConPlan,
  gruposConPlan,
  nutrienteDesdeSupplied,
  OxidoAgregado,
  urgenciaLabel,
  urgenciaOrden,
  urgenciaPorOxido,
  urgenciaProducto,
} from "../../../../utils/planCompraUtils";
import { IConnectRender } from "../../../definitions/IConnectRender";
import template from "./fertilizacionPlanCompraTemplate.html";

mustache.parse(template);

/** Contador para ids únicos por instancia. */
let FERTI_PLAN_ID = 0;

/** Tipos de nutriente que Nitrofert **no** resuelve como producto: van siempre
 *  al bloque "por fuera del portafolio". */
const TIPO_LIMING = "liming";
const TIPO_ORGANICO = "organic_amendment";

/** Etiquetas de `nutrientType`. */
const TIPO_LABEL: Record<string, string> = {
  liming: "Encalado",
  organic_amendment: "Enmienda orgánica",
  chemical: "Químico",
};

/** Badge por tipo de nutriente. */
const TIPO_BADGE: Record<string, string> = {
  liming: "sx-badge-warning",
  organic_amendment: "sx-badge-success",
  chemical: "sx-badge-info",
};

/** Cuerpo del plan de compra (Render/Connect). Dos modos sobre la misma
 *  pantalla:
 *  - **sin `compra[]`** (modo Sextante): una única lista consolidada por óxido,
 *    que es la especificación técnica sin marca;
 *  - **con `compra[]`** (modo Nitrofert): productos consolidados + por grupo,
 *    más el bloque "a conseguir por fuera del portafolio" — cal, materia
 *    orgánica y los óxidos que el portafolio no cubre.
 *
 *  Lo único que necesita listeners son las dos pestañas. */
export class FertilizacionPlanCompraRender implements IConnectRender {
  /** Id único de la instancia. */
  private readonly _id: number;
  /** Grupos con su plan comercial parseado (vacío en modo Sextante). */
  private readonly _grupos: GrupoConPlan[];

  /**
   * @param _analisis Análisis con la recomendación ya generada.
   * @param _lotes    Lotes de la finca (para nombrar los que consume cada ítem).
   */
  constructor(
    private readonly _analisis: AnalisisLaboratorioResponse,
    private readonly _lotes: ClienteFincaLoteResponse[],
  ) {
    FERTI_PLAN_ID += 1;
    this._id = FERTI_PLAN_ID;
    this._grupos = gruposConPlan(_analisis);
  }

  render(): string {
    const conProductos = this._analisis.compra.length > 0;
    const porFuera = conProductos
      ? this._buildPorFueraNitrofert()
      : this._buildTodoPorOxido();

    return mustache.render(template, {
      id: this._id,
      kpis: this._buildKpis(conProductos, porFuera.length),
      warnings: this._warnings(),
      conProductos,
      productos: conProductos ? this._buildProductos() : [],
      totalKg: _2d(
        this._analisis.compra.reduce((total, item) => total + item.kgTotal, 0),
      ),
      totalBultos: this._analisis.compra.reduce(
        (total, item) => total + item.bags50Kg,
        0,
      ),
      grupos: conProductos ? this._buildGrupos() : [],
      porFuera,
      porFueraTitulo: conProductos
        ? "A conseguir por fuera del portafolio"
        : "Requerimiento de la finca, por nutriente",
      porFueraSub: conProductos
        ? "Cal, materia orgánica y lo que el portafolio no cubre"
        : "Especificación técnica sin marca · en óxidos",
      tieneContenido: conProductos || porFuera.length > 0,
    });
  }

  connect(): void {
    this._tabsEl = document.getElementById(`ferti-plan-tabs-${this._id}`);
    this._tabsEl?.addEventListener("click", this._onTabClick);
  }

  dispose(): void {
    this._tabsEl?.removeEventListener("click", this._onTabClick);
    this._tabsEl = null;
  }

  private _tabsEl: HTMLElement | null = null;

  /** Cambia de pestaña: marca el botón y muestra su panel. */
  private _onTabClick = (event: Event): void => {
    const btn = (event.target as HTMLElement).closest<HTMLElement>("[data-tab]");
    const tab = btn?.getAttribute("data-tab");
    if (!tab || !this._tabsEl) return;

    this._tabsEl
      .querySelectorAll("[data-tab]")
      .forEach((el) => el.classList.remove("ferti-plan-tab--active"));
    btn?.classList.add("ferti-plan-tab--active");

    const contenedor = this._tabsEl.parentElement;
    contenedor?.querySelectorAll<HTMLElement>("[data-panel]").forEach((el) => {
      el.style.display = el.getAttribute("data-panel") === tab ? "" : "none";
    });
  };

  /** KPIs: área analizada, ítems a cotizar, kg totales y bultos (estos dos
   *  últimos solo tienen sentido con productos). */
  private _buildKpis(
    conProductos: boolean,
    itemsPorFuera: number,
  ): { label: string; valor: string; sub: string }[] {
    const registros = this._analisis.registros.filter(
      (registro) => registro.loteId !== null,
    );
    const area = registros.reduce(
      (total, registro) => total + (registro.planAreaHa ?? 0),
      0,
    );

    if (!conProductos) {
      const kg = this._buildTodoPorOxido().reduce(
        (total, item) => total + item.kgNumero,
        0,
      );
      return [
        {
          label: "Área analizada",
          valor: _2d(area),
          sub: `hectáreas · ${registros.length} lotes`,
        },
        {
          label: "Nutrientes a cubrir",
          valor: `${itemsPorFuera}`,
          sub: "en óxidos, sin marca",
        },
        { label: "Total estimado", valor: _2d(kg), sub: "kg de nutriente" },
      ];
    }

    const kgTotal = this._analisis.compra.reduce(
      (total, item) => total + item.kgTotal,
      0,
    );
    const bultos = this._analisis.compra.reduce(
      (total, item) => total + item.bags50Kg,
      0,
    );
    return [
      {
        label: "Área analizada",
        valor: _2d(area),
        sub: `hectáreas · ${registros.length} lotes`,
      },
      {
        label: "Productos a cotizar",
        valor: `${this._analisis.compra.length}`,
        sub: `+ ${itemsPorFuera} por fuera del portafolio`,
      },
      { label: "Total estimado", valor: _2d(kgTotal), sub: "kg de producto" },
      { label: "Empaques", valor: `${bultos}`, sub: "bultos de 50 kg" },
    ];
  }

  /** Avisos de L3 de todos los grupos (vienen en castellano, listos para
   *  mostrar), sin repetir. */
  private _warnings(): string[] {
    const todos = this._grupos.flatMap((grupo) => grupo.plan?.warnings ?? []);
    return [...new Set(todos)];
  }

  /** Filas de la lista consolidada de productos Nitrofert, **lo más urgente
   *  primero** y, a igual urgencia, lo de mayor tonelaje. */
  private _buildProductos(): {
    product: string;
    line: string | null;
    clFree: boolean;
    urgencia: string;
    urgenciaClase: string;
    kgTotal: string;
    bultos: number;
    area: string;
    lotes: string;
    orden: number;
    kgNumero: number;
  }[] {
    const loteById = new Map(this._lotes.map((lote) => [lote.id, lote]));
    const urgencias = urgenciaPorOxido(this._analisis.registros);

    const filas = this._analisis.compra.map((item) => {
      const urgencia = this._urgenciaDeProducto(item.product, urgencias);
      // `loteIds` puede traer menos elementos que `lots` si algún nombre no
      // matcheó: se cae a los nombres crudos del microservicio.
      const nombres = item.loteIds
        .map((id) => loteById.get(id)?.nombreLote)
        .filter((nombre) => nombre !== undefined);
      return {
        product: item.product,
        line: item.line,
        clFree: item.clFree,
        urgencia: urgenciaLabel(urgencia),
        urgenciaClase: this._urgenciaClase(urgencia),
        kgTotal: _2d(item.kgTotal),
        bultos: item.bags50Kg,
        area: `${_2d(item.areaConsumidaHa)} ha`,
        lotes: (nombres.length > 0 ? nombres : item.lots).join(" · "),
        orden: urgenciaOrden(urgencia),
        kgNumero: item.kgTotal,
      };
    });

    return filas.sort((a, b) => a.orden - b.orden || b.kgNumero - a.kgNumero);
  }

  /** Urgencia de un producto consolidado: la más alta entre las instancias del
   *  mismo producto en los planes por grupo (el consolidado no trae los óxidos
   *  que aporta). Si no aparece en ningún plan, queda sin dato. */
  private _urgenciaDeProducto(
    product: string,
    urgencias: Map<string, string>,
  ): string {
    let mejor: string | null = null;
    for (const grupo of this._grupos) {
      for (const producto of grupo.plan?.productos ?? []) {
        if (producto.product !== product) continue;
        const urgencia = urgenciaProducto(producto, urgencias);
        if (mejor === null || urgenciaOrden(urgencia) < urgenciaOrden(mejor)) {
          mejor = urgencia;
        }
      }
    }
    return mejor ?? "next_season";
  }

  /** Cards de la vista por grupo, con los productos del `nitrofertPlan`,
   *  también con lo más urgente primero. */
  private _buildGrupos(): {
    titulo: string;
    lotes: string;
    area: string;
    error: string | null;
    productos: {
      product: string;
      note: string | null;
      urgencia: string;
      urgenciaClase: string;
      kgHa: string;
      kgTotal: string;
      aporta: string;
      orden: number;
    }[];
  }[] {
    const loteById = new Map(this._lotes.map((lote) => [lote.id, lote]));
    const urgencias = urgenciaPorOxido(this._analisis.registros);

    return this._grupos.map((grupo, index) => {
      const nombres = grupo.registros
        .map((registro) =>
          registro.loteId ? loteById.get(registro.loteId)?.nombreLote : undefined,
        )
        .filter((nombre) => nombre !== undefined);

      return {
        titulo: `Grupo ${index + 1}`,
        lotes: nombres.join(" · ") || grupo.plan?.loteReferencia || "—",
        area: `${_2d(grupo.totalAreaHa)} ha`,
        error: grupo.l3Error
          ? `No se pudo calcular el plan comercial: ${grupo.l3Error}`
          : null,
        productos: (grupo.plan?.productos ?? [])
          .map((producto) => {
            const urgencia = urgenciaProducto(producto, urgencias);
            return {
              product: producto.product,
              note: producto.note,
              urgencia: urgenciaLabel(urgencia),
              urgenciaClase: this._urgenciaClase(urgencia),
              kgHa: producto.kgHa != null ? `${_2d(producto.kgHa)} kg/ha` : "—",
              kgTotal: producto.kgTotal != null ? _2d(producto.kgTotal) : "—",
              aporta: Object.entries(producto.nutrientsSupplied)
                .map(
                  ([clave, kg]) =>
                    `${nutrienteSimbolo(nutrienteDesdeSupplied(clave))} ${_2d(kg)}`,
                )
                .join(" · "),
              orden: urgenciaOrden(urgencia),
            };
          })
          .sort((a, b) => a.orden - b.orden),
      };
    });
  }

  /** Modo Sextante: toda la lista en óxidos, sin marca. */
  private _buildTodoPorOxido(): FilaPorFuera[] {
    return agregarPorOxido(this._analisis.registros).map((agregado) =>
      this._filaOxido(agregado, null),
    );
  }

  /** Modo Nitrofert: cal + materia orgánica (que el portafolio no resuelve por
   *  contrato) más los óxidos que quedaron sin cubrir o cubiertos a medias. */
  private _buildPorFueraNitrofert(): FilaPorFuera[] {
    const enmiendas = agregarPorOxido(
      this._analisis.registros,
      (nutriente) =>
        nutriente.nutrientType === TIPO_LIMING ||
        nutriente.nutrientType === TIPO_ORGANICO,
    ).map((agregado) => this._filaOxido(agregado, null));

    const porOxido = new Map(
      agregarPorOxido(this._analisis.registros).map((agregado) => [
        agregado.nutrient,
        agregado,
      ]),
    );

    const faltantes: FilaPorFuera[] = [];
    for (const faltante of faltantesPorOxido(this._grupos)) {
      const agregado = porOxido.get(faltante.nutrient);
      // La cal y la MO ya están arriba: no duplicar.
      if (
        !agregado ||
        agregado.nutrientType === TIPO_LIMING ||
        agregado.nutrientType === TIPO_ORGANICO
      ) {
        continue;
      }
      faltantes.push(
        this._filaOxido(
          { ...agregado, kgTotal: faltante.kgFaltante },
          faltante.sinProducto
            ? "Sin producto en el portafolio"
            : `Cubierto al ${faltante.coberturaPct}% — falta el resto`,
        ),
      );
    }

    return [...enmiendas, ...faltantes].sort(
      (a, b) => a.orden - b.orden || b.kgNumero - a.kgNumero,
    );
  }

  /** Fila del bloque por óxido. */
  private _filaOxido(
    agregado: OxidoAgregado,
    detalle: string | null,
  ): FilaPorFuera {
    return {
      nutriente: nutrienteSimbolo(agregado.nutrient),
      detalle,
      tipo: TIPO_LABEL[agregado.nutrientType] ?? agregado.nutrientType,
      tipoBadge: TIPO_BADGE[agregado.nutrientType] ?? "sx-badge-neutral",
      urgencia: urgenciaLabel(agregado.urgency),
      urgenciaClase: this._urgenciaClase(agregado.urgency),
      kgTotal: _2d(agregado.kgTotal),
      kgNumero: agregado.kgTotal,
      lotes: `${agregado.lotes} lote(s)`,
      orden: urgenciaOrden(agregado.urgency),
    };
  }

  /** Resalta la urgencia inmediata. */
  private _urgenciaClase(urgency: string): string {
    return urgency === "immediate" ? "ferti-plan-urgente" : "";
  }
}

/** Fila del bloque en óxidos (modo Sextante completo, o "por fuera" en
 *  Nitrofert). */
type FilaPorFuera = {
  nutriente: string;
  detalle: string | null;
  tipo: string;
  tipoBadge: string;
  urgencia: string;
  urgenciaClase: string;
  kgTotal: string;
  /** Valor numérico, para ordenar y sumar. */
  kgNumero: number;
  lotes: string;
  orden: number;
};
